import { datadogLogs } from "@datadog/browser-logs";
import { GetReturnErrorCode } from "@redotech/customer-portal-sdk/rpc/schema/get-return";
import { Currency } from "@redotech/money/currencies";
import { useLoad } from "@redotech/react-util/load";
import { getResource } from "@redotech/redo-model/localization/resource";
import { Order } from "@redotech/redo-model/order-schema";
import {
  allProductsAreRepair,
  ReturnAddressZod,
  ReturnTypeEnum,
  ReturnWarning,
  ReturnZod,
  UpsellBillingTrigger,
} from "@redotech/redo-model/return";
import { PickupPayer } from "@redotech/redo-model/return-flow";
import { ReturnStatus } from "@redotech/redo-model/return-status";
import {
  getReturnTotalsFrontendLogFn,
  ReturnTotals,
  ReturnTotalsCalculator,
} from "@redotech/redo-model/return-totals-calculator";
import {
  getFieldsFromReturnTotalsSet,
  RAC_LOG_STAMP,
  ReturnTotalsType,
} from "@redotech/redo-model/returns/return-totals/return-totals-set";
import { SerializedReturnSummaryTotals } from "@redotech/redo-model/returns/return-totals/types";
import { returnToTrackable } from "@redotech/redo-model/trackable";
import { ClientError } from "@redotech/rpc/errors";
import { filterTruthy } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import { useContext, useEffect, useMemo } from "react";
import { RpcClientContext } from "../../contexts/rpc";
import { ReturnAddressType } from "../../navigator";
import { CustomerPortalAppSettings } from "../../settings";
import {
  getExchangeRateAtTimeOfOrder,
  ReturnAddressToAddress,
  ReturnItemParams,
  subtitleWithoutDefault,
} from "../../util";
import { AddressInfo } from "../useAddress/use-address";
import {
  useCreationStatus,
  UseCreationStatusResult,
} from "./use-creation-status";
import { usePickupModals } from "./use-pickup-modals";
import { useSteps, UseStepsResponse } from "./use-steps";
export enum TopStatus {
  SUCCESS = "success",
  WARNING = "warning",
  ERROR = "error",
}

export enum ReturnSubmissionResultStatus {
  CREATED_RETURN = "successfully_created_return",
  CREATED_RETURN_WITH_ERRORS = "created_return_with_errors",
  REJECTED_RETURN = "rejected_return",
}

const returnTypeToReturnItems: Record<ReturnTypeEnum, string> = {
  [ReturnTypeEnum.RETURN]: "Return Details",
  [ReturnTypeEnum.CLAIM]: "Claim Details",
  [ReturnTypeEnum.WARRANTY]: "Warranty Details",
  [ReturnTypeEnum.RECYCLE]: "Recycle Details",
};

export type ErrorCodeInfo =
  | {
      type: GetReturnErrorCode.ReturnNotFound;
      data: { texts: { title: string } };
    }
  | undefined;

export type AlertProps = {
  title: string;
  subtitle: string;
  primaryAction: { onClick: () => void; text: string } | undefined;
  secondaryAction: { onClick: () => void; text: string } | undefined;
};
interface ReturnConfirmationResponse extends UseStepsResponse {
  returnHeader: {
    title: string;
    subtitle: string;
    onClick: (() => void) | undefined;
    onClickTitle: string | undefined;
    status: TopStatus;
    alerts: AlertProps[] | undefined;
  };
  error: string | undefined;
  errorCodeData: ErrorCodeInfo | undefined;
  loadingReturn: boolean;
  rejected: boolean;
  rejectHtml: string;
  customSuccessMessage: string | undefined;
  warnings: ReturnWarning[] | undefined;
  deadlineDays: number;
  showDeadline: boolean | undefined;
  newItems: ReturnItemParams[] | undefined;
  products: ReturnItemParams[] | undefined;
  currency: Currency;
  newItemsHeader: string;
  showAddresses: boolean;
  newOrderAddress: AddressInfo | undefined;
  customerInfo: {
    firstName: string;
    lastName: string;
    email: string;
    id: string;
  };
  returnAddress: AddressInfo | undefined;
  returnTotals: ReturnTotals | undefined;
  summaryFeesHeader: string;
  summaryFeesOrderHeader: string;
  returnItemsHeader: string;
  draftOrderUrl: string | undefined;
  texts: { rejectedText: string; returnItems: string; newItems: string };
  labelDeducted: boolean;
  creationStatus: UseCreationStatusResult["creationStatus"];
  showCreationStatus: boolean;
  returnType: ReturnTypeEnum;

  shouldShowSummary: boolean;
  manualLabelInfo: {
    showManualLabel: boolean;
    instructions: string[] | undefined;
  };
  manualLabelData?: {
    shipments?: Array<{
      address: any;
      returnItemIds: string[];
      packingSlipUrl?: string | null;
    }> | null;
  } | null;
  showReturnSteps: boolean;
  upsellBillingTrigger?: UpsellBillingTrigger | null;
  presentmentCurrency: string;
  exchangeRate: number;
  returnData: ReturnZod | undefined;
  pickupModals: ReturnType<typeof usePickupModals>;
}

export const useReturnConfirmation = ({
  settings,
  returnId,
  navigateToCheckout,
  isMerchantCreated = false,
  useTemporalCreationStatus = true,
}: {
  settings: CustomerPortalAppSettings | undefined;
  returnId: string;
  navigateToCheckout: (orderUrl: string) => void;
  isMerchantCreated?: boolean;
  useTemporalCreationStatus?: boolean;
}): ReturnConfirmationResponse => {
  const client = useContext(RpcClientContext);

  const creationStatusResult = useCreationStatus({ returnId });
  const showCreationStatus = !creationStatusResult.completelyFinished;

  useEffect(() => {
    if (showCreationStatus || !isMerchantCreated) {
      return;
    }
    const targetOrigin =
      process.env.NODE_ENV === "production"
        ? "https://app.getredo.com"
        : "http://localhost:3003";
    window?.top?.postMessage({ returnId }, targetOrigin);
  }, [showCreationStatus, returnId, isMerchantCreated]);

  const returnLoad = useLoad(
    async (signal) => {
      try {
        if (
          !client ||
          !returnId ||
          (!creationStatusResult.completelyFinished &&
            useTemporalCreationStatus)
        ) {
          return {
            returnData: undefined,
            orders: undefined,
            originOrders: undefined,
            loyaltyData: undefined,
          };
        }
        const {
          return: returnData,
          originOrders,
          loyaltyData,
        } = await client.getReturn({ returnId });
        const { orders } = (await client.getOrders({
          orderIds: returnData.orders.map((order) => order.order),
        })) as unknown as { orders: Order[] };

        return { returnData, orders, originOrders, loyaltyData };
      } catch (e: any) {
        if (e instanceof ClientError) {
          datadogLogs.logger.error(
            "Client error retrieving return after submission",
            {
              code: e.code,
              statusCode: e.statusCode,
              message: e.message,
              returnId,
            },
          );
        }
        datadogLogs.logger.error("Error retrieving return after submission", {
          e,
          returnId,
        });
        if (e?.code) {
          const returnErrorCode = e.code as GetReturnErrorCode;
          switch (returnErrorCode) {
            case GetReturnErrorCode.ReturnNotFound:
              throw e;
            default:
              assertNever(returnErrorCode);
          }
        }
        if (e?.message?.includes("Output validation")) {
          throw new Error(
            `Validation Error: Your return was submitted successfully, but there was an issue parsing your return, please contact support at support@getredo.com with the return id ${returnId}`,
          );
        }

        throw new Error(
          "Your return was submitted successfully, but there was an issue loading your return. Please refresh the page to load your return.",
        );
      }
    },
    [
      client,
      returnId,
      creationStatusResult.completelyFinished,
      useTemporalCreationStatus,
    ],
  );

  const { returnData, orders, originOrders, loyaltyData } =
    returnLoad.value ?? {};
  const rejected = returnData?.status === ReturnStatus.REJECTED;

  const presentmentCurrency = returnData?.presentmentCurrency ?? Currency.USD;
  const exchangeRate =
    getExchangeRateAtTimeOfOrder(
      returnLoad?.value?.orders?.[0]?.shopify?.line_items,
    ) ?? 1;

  const baseCurrency = returnData?.currency ?? Currency.USD;
  const shouldConvertCurrency = !!(
    exchangeRate &&
    exchangeRate > 0 &&
    presentmentCurrency &&
    presentmentCurrency !== baseCurrency
  );

  const returnTotals = useLoad(async () => {
    if (
      !returnData ||
      !settings ||
      !orders ||
      !loyaltyData ||
      !originOrders ||
      !client
    ) {
      return undefined;
    }

    let convertedReturnData = returnData;
    if (shouldConvertCurrency && exchangeRate) {
      convertedReturnData = {
        ...returnData,
        products: returnData.products.map((product) => ({
          ...product,
          price: product.variant_id
            ? (+product.price * exchangeRate).toString()
            : product.price,
        })),
        advancedExchangeItems: returnData.advancedExchangeItems?.map(
          (item) => ({
            ...item,
            price: item.variantId
              ? (
                  +(item.price || 0) * (returnData.newItemToExchangeRate ?? 1)
                ).toString()
              : item.price,
          }),
        ),
        new_order_taxes: (
          +(returnData.new_order_taxes || "0") *
          (returnData.newItemToExchangeRate ?? 1)
        ).toString(),
      };
    }

    const calculator = new ReturnTotalsCalculator({
      return_: convertedReturnData as any, //TODO type this
      order: orders[0] as any,
      orders: orders as any,
      team: { settings },
      originOrders: originOrders ?? [{ error: "No origin order" }],
      loyaltyData,
      logFn: getReturnTotalsFrontendLogFn(),
      newItemToExchangeRate: returnData.newItemToExchangeRate ?? 1,
      isInPresentmentCurrency: shouldConvertCurrency,
    });
    const legacyTotals = calculator.getTotalsForAllProducts();

    let returnSummaryTotals: SerializedReturnSummaryTotals | undefined =
      undefined;
    let returnTotalsTypeToUse: ReturnTotalsType = ReturnTotalsType.LEGACY;
    try {
      const response = await client.getReturnTotals(
        { returnId: returnData._id },
        { timeout: { seconds: 1 } },
      );
      returnSummaryTotals = response.returnSummaryTotals;
      returnTotalsTypeToUse = response.returnTotalsTypeToUse;
    } catch (error) {
      console.error(`${RAC_LOG_STAMP} Failed to get return totals`, error);
    }
    const returnTotalsSet = {
      legacy: legacyTotals,
      new: returnSummaryTotals,
      typeToUse: returnTotalsTypeToUse,
    };

    return getFieldsFromReturnTotalsSet({ returnTotalsSet });
  }, [
    orders,
    returnData,
    settings,
    originOrders,
    loyaltyData,
    shouldConvertCurrency,
    exchangeRate,
    client,
  ]);

  const newItems = useMemo(() => {
    if (!returnData || !returnTotals.value) {
      return [];
    }

    const items: ReturnItemParams[] = [];

    // Advanced exchange items
    if (returnData.advancedExchangeItems) {
      items.push(
        ...returnData.advancedExchangeItems.map((item) => ({
          id: item.variantId,
          pendingReturnItemId: "",
          imageSrc: item.images?.[0] ?? "",
          title: item.title ?? "",
          subtitle: item.variantTitle ?? "",
          quantity: item.quantity ?? 1,
          variants: [],
          formattedPrice: item.price ?? "0",
          isSelected: true,
          currency: shouldConvertCurrency
            ? presentmentCurrency
            : (returnData.currency ?? Currency.USD),
          price: item.price ?? "0",
          itemValue: item.itemValue ?? "0",
          presentmentCurrency: String(presentmentCurrency),
          exchangeRate: returnData.newItemToExchangeRate ?? 1,
          collections: [],
        })),
      );
    }

    // Variant exchange items from products
    for (const product of returnData.products) {
      const productTotals = returnTotals.value?.productTotals.find(
        (pt) =>
          pt.product.variant_id === product.variant_id &&
          pt.product.line_item_id === product.line_item_id,
      );

      const price = productTotals?.originalPrice?.toString() ?? "0";
      const itemValue = productTotals?.discountPrice?.toString() ?? "0";

      if (product.exchange_for) {
        items.push({
          id: product.exchange_for.variant_id,
          pendingReturnItemId: "",
          imageSrc: product.exchange_for.images?.[0] ?? "",
          title: product.exchange_for.product_title,
          subtitle: product.exchange_for.variant_title,
          quantity: product.quantity ?? 1,
          variants: [],
          formattedPrice: price,
          isSelected: true,
          currency: returnData.currency ?? Currency.USD,
          price,
          itemValue,
          presentmentCurrency: String(presentmentCurrency),
          exchangeRate: returnData.newItemToExchangeRate ?? 1,
          collections: [],
        });
      }

      if (product.exchangeGroupItem) {
        items.push({
          id: product.exchangeGroupItem.variantId,
          pendingReturnItemId: "",
          imageSrc: product.exchangeGroupItem.imageSrc ?? "",
          title: product.exchangeGroupItem.title,
          subtitle: product.exchangeGroupItem.variantTitle,
          quantity: product.quantity ?? 1,
          variants: [],
          formattedPrice: price,
          isSelected: true,
          currency: returnData.currency ?? Currency.USD,
          price,
          itemValue,
          presentmentCurrency: String(presentmentCurrency),
          exchangeRate: returnData.newItemToExchangeRate ?? 1,
          collections: [],
        });
      }
    }

    return items;
  }, [returnData, returnTotals, presentmentCurrency, shouldConvertCurrency]);

  /**
   * Return warnings are displayed to the customer with a support code, but are not actionable
   */
  const warnings = useMemo(() => {
    // Filter our irrelevant warnings
    return returnData?.warnings?.filter((warning: ReturnWarning) => {
      if (warning.type === "label" && returnData.shipment) {
        return false;
        // All pickup warnings will be converted to alerts
      } else if (warning.type === "pickup") {
        return false;
      } else if (
        warning.type === "instant-refund" &&
        returnData.instantRefund
      ) {
        return false;
        // Claim report warnings are internal and should not be shown to the customer
      } else if (warning.type === "claim-report") {
        return false;
      }
      return true;
    });
  }, [returnData]);

  const pickupModals = usePickupModals({ returnData });

  const hasUnresolvedPickupWarnings = useMemo(() => {
    return (
      returnData?.warnings?.some((warning) => warning.type === "pickup") &&
      !!returnData.pickup &&
      !returnData.shipment?.pickup
    );
  }, [returnData]);

  /**
   * Alerts are warnings that have specific actions that the customer can take to resolve the underlying issue
   */
  const alerts: AlertProps[] | undefined = useMemo(() => {
    if (hasUnresolvedPickupWarnings) {
      const isMerchantCoveredPickup =
        returnData?.pickup?.pickupPayer === PickupPayer.MERCHANT;
      return [
        {
          title: "Package pickup failed",
          subtitle: isMerchantCoveredPickup
            ? "Your return was successfully submitted, but we couldn't schedule your package pickup. Please try again or cancel the pickup."
            : "Your return was successfully submitted, but we couldn't schedule your package pickup. Please try again or cancel the pickup. If you don't reschedule, the pickup fee will be refunded once your item arrives at our warehouse.",
          primaryAction: {
            onClick: () => {
              pickupModals.setRescheduleModalOpen(true);
            },
            text: "Try again",
          },
          secondaryAction: {
            onClick: () => {
              pickupModals.setCancelModalOpen(true);
            },
            text: isMerchantCoveredPickup
              ? "Cancel pickup"
              : "Cancel and refund pickup",
          },
        },
      ];
    }
    return undefined;
  }, [hasUnresolvedPickupWarnings, returnData, pickupModals]);

  const copyResource = getResource({
    variant: returnData?.type || "return",
    overrides: settings?.resourceOverride,
  });

  const returnSubmissionResultStatus = useMemo(() => {
    if (!warnings) {
      return undefined;
    } else if (rejected) {
      return ReturnSubmissionResultStatus.REJECTED_RETURN;
    } else if (warnings?.length) {
      return ReturnSubmissionResultStatus.CREATED_RETURN_WITH_ERRORS;
    } else {
      return ReturnSubmissionResultStatus.CREATED_RETURN;
    }
  }, [rejected, warnings]);

  const needToPayDraftOrder =
    returnData?.type === "return" &&
    returnData.draftOrderURL &&
    !returnData.exchangeOrder?.length &&
    returnData.upsellBillingTrigger !== UpsellBillingTrigger.RETURN_CREATED;

  const returnHeader = useMemo(() => {
    if (needToPayDraftOrder) {
      return {
        title: copyResource.DRAFT_ORDER_HEADER,
        subtitle: copyResource.DRAFT_ORDER_SUBTITLE,
        onClick: () => navigateToCheckout(returnData?.draftOrderURL ?? ""),
        onClickTitle: "Pay for exchange order",
        status: TopStatus.WARNING,
        alerts,
      };
    }
    switch (returnSubmissionResultStatus) {
      case ReturnSubmissionResultStatus.CREATED_RETURN:
        return {
          title: copyResource.CONFIRMATION_HEADER_TEXT_SUBMIT,
          subtitle: copyResource.CONFIRMATION_HEADER_SUBTEXT,
          onClick: undefined,
          onClickTitle: undefined,
          status: TopStatus.SUCCESS,
          alerts,
        };
      case ReturnSubmissionResultStatus.CREATED_RETURN_WITH_ERRORS:
        return {
          title: copyResource.CONFIRMATION_HEADER_TEXT_SUBMIT_WITH_ERRORS,
          subtitle: copyResource.CONFIRMATION_HEADER_SUBTEXT,
          onClick: undefined,
          onClickTitle: undefined,
          status: TopStatus.ERROR,
          alerts,
        };
      case ReturnSubmissionResultStatus.REJECTED_RETURN:
        return {
          title: copyResource.CONFIRMATION_HEADER_TEXT_REJECT,
          subtitle: copyResource.CONFIRMATION_HEADER_SUBTEXT,
          onClick: undefined,
          onClickTitle: undefined,
          status: TopStatus.ERROR,
          alerts: undefined,
        };
      default:
        return {
          title: "Something went wrong",
          subtitle: "Please exit out and try again.",
          onClick: undefined,
          onClickTitle: undefined,
          status: TopStatus.ERROR,
          alerts: undefined,
        };
    }
  }, [
    navigateToCheckout,
    returnSubmissionResultStatus,
    copyResource,
    needToPayDraftOrder,
    returnData?.draftOrderURL,
    alerts,
  ]);

  const trackable =
    returnData && orders && orders[0]
      ? returnToTrackable(returnData as any, orders[0] as any) //TODO, eventually type this function
      : undefined;

  const stepData = useSteps({
    settings,
    trackable,
    hasUnresolvedPickupWarnings: hasUnresolvedPickupWarnings ?? false,
    upsellBillingTrigger: returnData?.upsellBillingTrigger ?? undefined,
  });

  const deadlineDays = Math.max(
    0,
    Math.round(
      Temporal.Now.instant()
        // FIXME Our types are all wonky - expirationDate is really a string, not a Date object
        .until(
          returnData?.expirationDate?.toTemporalInstant() ??
            Temporal.Now.instant(),
        )
        .total("days"),
    ),
  );
  const showDeadline =
    returnData?.products.some((product) => !product.green_return) &&
    !!returnData?.expirationDate &&
    deadlineDays <= 10;

  const isExchange =
    returnData?.advancedExchangeItems?.length ||
    returnData?.products.some(
      (product) => product.exchange_for || product.exchangeGroupItem,
    );

  const showAddresses = !!returnData?.shipping_address;
  const showNewOrderAddress =
    showAddresses && !!isExchange && !!returnData.exchangeOrder?.length;

  function addressToInfo(
    address: ReturnAddressZod,
    title: string,
    type: ReturnAddressType,
  ): AddressInfo {
    return {
      address: ReturnAddressToAddress(address),
      title,
      addressText1: address.name || "",
      addressText2: address.address1,
      addressText3: address.address2 ?? undefined,
      addressText4: filterTruthy([
        address.city,
        address.province,
        address.zip,
      ]).join(", "),
      type,
    };
  }
  const returnAddress: AddressInfo | undefined = returnData?.shipping_address
    ? addressToInfo(
        returnData.shipping_address,
        "Return items from:",
        ReturnAddressType.SHIPPING,
      )
    : undefined;

  let newOrderAddressInfo: AddressInfo | undefined;
  if (showNewOrderAddress) {
    if (returnData.newOrderAddress) {
      newOrderAddressInfo = addressToInfo(
        returnData.newOrderAddress,
        "Shipping new items to:",
        ReturnAddressType.NEW_ORDER,
      );
    } else if (returnData.shipping_address) {
      newOrderAddressInfo = addressToInfo(
        returnData.shipping_address,
        "Shipping new items to:",
        ReturnAddressType.NEW_ORDER,
      );
    }
  }

  const errorCode = returnLoad.error?.code as GetReturnErrorCode | undefined;

  let errorCodeData: ErrorCodeInfo | undefined;
  switch (errorCode) {
    case GetReturnErrorCode.ReturnNotFound:
      errorCodeData = {
        type: GetReturnErrorCode.ReturnNotFound,
        data: { texts: { title: "We we're unable to locate your return." } },
      };
      break;
    case undefined:
      errorCodeData = undefined;
      break;
    default:
      assertNever(errorCode);
  }

  const returnType = returnData?.type
    ? (returnData.type as ReturnTypeEnum)
    : ReturnTypeEnum.RETURN;

  const displayCurrency = shouldConvertCurrency
    ? presentmentCurrency
    : baseCurrency;

  return {
    ...stepData,
    returnHeader,
    error: returnLoad.error?.message as string | undefined,
    errorCodeData,
    //TODO: Danny refactor this mess
    loadingReturn: useTemporalCreationStatus
      ? returnLoad.pending || returnLoad.value === undefined
      : returnLoad.value?.returnData === undefined,

    rejected,

    rejectHtml: returnData?.products[0]?.rejectMessage ?? "",
    customSuccessMessage: settings?.theme.custom_confirmation_text,
    warnings,
    deadlineDays,
    showDeadline,
    newItems,
    products: returnData?.products.map(
      function toParams(product): ReturnItemParams {
        // Find the corresponding ProductTotals to get the correct originalPrice
        const productTotals = returnTotals.value?.productTotals?.find(
          (pt) =>
            pt.product.line_item_id?.toString() ===
            product.line_item_id?.toString(),
        );

        // Use originalPrice from ProductTotals if available, otherwise fallback to product.price
        const originalPrice =
          productTotals?.originalPrice?.toString() ?? product.price;

        return {
          id: product._id,
          pendingReturnItemId: product._id,
          imageSrc: product.images[0] ?? "",
          title: product.product_title,
          subtitle: subtitleWithoutDefault(product.variant_title),
          quantity: product.quantity,
          variants: [],
          formattedPrice: originalPrice,
          isSelected: true,
          currency: displayCurrency,
          price: originalPrice,
          itemValue: productTotals?.discountPrice?.toString() ?? "0",
          presentmentCurrency: String(presentmentCurrency),
          exchangeRate,
          collections: [],
        };
      },
    ),
    currency: displayCurrency,
    newItemsHeader: copyResource.NEW_ITEMS_SECTION_HEADER,
    showAddresses,
    newOrderAddress: newOrderAddressInfo,
    customerInfo: {
      firstName: returnData?.shipping_address?.name?.split(" ")[0] ?? "",
      lastName: returnData?.shipping_address?.name?.split(" ")[1] ?? "",
      email: returnData?.shipping_address?.email ?? "",
      id: returnData?.customer?.shopify_customer_id ?? "",
    },
    returnAddress,
    summaryFeesHeader: copyResource.FEES_HEADER,
    returnTotals: returnTotals.value,
    summaryFeesOrderHeader: copyResource.FEES_ORDER_HEADER,
    returnItemsHeader: copyResource.RETURN_ITEMS_HEADER,
    draftOrderUrl: returnData?.draftOrderURL,
    texts: {
      rejectedText: "This item was rejected",
      returnItems: returnTypeToReturnItems[returnType],
      newItems: "New items",
    },
    labelDeducted: returnData?.labelDeductedFromCredit ?? false,
    showCreationStatus,
    creationStatus: creationStatusResult.creationStatus,
    returnType,
    shouldShowSummary: !allProductsAreRepair(returnData?.products),
    manualLabelInfo: {
      showManualLabel: returnData?.isManualLabel === true,
      instructions: settings?.returns?.manualLabelInstructions,
    },
    manualLabelData: returnData?.manualLabelData,
    showReturnSteps: !returnData?.isManualLabel,
    upsellBillingTrigger:
      returnData?.upsellBillingTrigger as UpsellBillingTrigger,
    presentmentCurrency: String(displayCurrency),
    exchangeRate,
    returnData,
    pickupModals,
  };
};
